import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Box, Checkbox, Link, useTheme } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { apiListColleges, type College } from '../../services/api';
import { LEGAL_COOKIE_VERSION, LEGAL_PRIVACY_VERSION, LEGAL_TERMS_VERSION } from '../../constants';
import { authTokens, emberGradient, FONT_DISPLAY, FONT_MONO } from './brandTokens';
import { BrandMark } from '../Brand/Logo';

type Mode = 'signin' | 'signup';

const MAJORS = [
  'Computer Science', 'Engineering', 'Business', 'Arts & Sciences', 'Nursing',
  'Public Health', 'Architecture', 'Design', 'Information Systems', 'Other',
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Matches the server password policy so we fail fast before the API rejects.
function passwordStrengthError(p: string): string | null {
  if (p.length < 8) return 'Use 8+ characters';
  if (!/[a-z]/.test(p)) return 'Add a lowercase letter';
  if (!/[A-Z]/.test(p)) return 'Add an uppercase letter';
  if (!/[0-9]/.test(p)) return 'Add a number';
  if (!/[^A-Za-z0-9]/.test(p)) return 'Add a symbol';
  return null;
}

const AuthForm: React.FC<{ defaultMode: Mode }> = ({ defaultMode }) => {
  const themeMode = useTheme().palette.mode === 'dark' ? 'dark' : 'light';
  const t = authTokens(themeMode);
  const { login, register, requestPasswordReset, loading, error, clearError } = useAuth();
  const navigate = useNavigate();

  const [mode, setMode] = useState<Mode>(defaultMode);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [emailLocal, setEmailLocal] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [major, setMajor] = useState('');
  const [year, setYear] = useState('');
  const [agree, setAgree] = useState(false);

  const [colleges, setColleges] = useState<College[]>([]);
  const [collegesStatus, setCollegesStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [college, setCollege] = useState<College | null>(null);
  const [schoolQuery, setSchoolQuery] = useState('');
  const [listOpen, setListOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const comboRef = useRef<HTMLDivElement | null>(null);

  const [errors, setErrors] = useState<Record<string, string>>({});

  const loadColleges = useCallback(() => {
    setCollegesStatus('loading');
    apiListColleges()
      .then((cs) => { setColleges(cs); setCollegesStatus('ready'); })
      .catch(() => setCollegesStatus('error'));
  }, []);

  useEffect(() => { clearError(); }, [mode, clearError]);
  useEffect(() => { loadColleges(); }, [loadColleges]);

  const isSignup = mode === 'signup';
  const domain = college?.emailDomains?.[0] ?? '';
  const filtered = useMemo(() => {
    const q = schoolQuery.trim().toLowerCase();
    return colleges.filter((c) => !q || c.name.toLowerCase().includes(q)).slice(0, 7);
  }, [colleges, schoolQuery]);
  // Show the panel whenever the field is open in sign-up — the panel itself renders the
  // loading / error / "no campus live yet" states so the caret is never a dead end.
  const showList = isSignup && listOpen;

  const chooseCollege = (u: College) => {
    if (blurTimer.current) clearTimeout(blurTimer.current);
    setCollege(u); setSchoolQuery(u.name); setListOpen(false); setHighlight(-1);
    clearErr('school'); clearErr('emailLocal');
  };

  // Keyboard combobox: arrow keys move the highlight, Enter selects it, Escape closes.
  // Focus stays on the input throughout, so there's no blur/close race.
  const onSchoolKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault(); setListOpen(true);
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter' && listOpen && highlight >= 0 && filtered[highlight]) {
      e.preventDefault(); chooseCollege(filtered[highlight]);
    } else if (e.key === 'Escape') {
      setListOpen(false); setHighlight(-1);
    }
  };

  const setErr = (k: string, v: string) => setErrors((e) => ({ ...e, [k]: v }));
  const clearErr = (k: string) => setErrors((e) => ({ ...e, [k]: '' }));

  const goMode = (m: Mode) => { setMode(m); setErrors({}); setResetSent(false); };

  // "Forgot?" (sign-in): email a reset link for whatever's in the email field.
  const onForgot = async (e: React.MouseEvent) => {
    e.preventDefault();
    const em = email.trim();
    if (!EMAIL_RE.test(em)) { setErr('email', 'Enter your email above first, then tap Forgot.'); return; }
    clearErr('email');
    setResetSent(true);
    await requestPasswordReset(em);
  };

  // ---- styles (transcribed from the design) --------------------------------
  const mono = { fontFamily: FONT_MONO, textTransform: 'uppercase' as const };
  const labelSx = { ...mono, fontSize: 10.5, letterSpacing: '0.13em', color: t.muted } as const;
  const errSx = { fontSize: 12, color: t.err, fontWeight: 600 } as const;
  const underline = (err?: string) => ({
    width: '100%', boxSizing: 'border-box', background: 'transparent', border: 'none',
    borderBottom: `1.5px solid ${err ? t.err : t.line}`, padding: '9px 2px', fontSize: 16,
    fontFamily: "'Hanken Grotesk', sans-serif", color: t.text, outline: 'none', borderRadius: 0,
    transition: 'border-color .2s', '&:focus': { borderBottomColor: t.ember },
    '&::placeholder': { color: t.muted, opacity: 0.8 },
  });
  const row = (err?: string) => ({
    display: 'flex', alignItems: 'center', gap: '6px',
    borderBottom: `1.5px solid ${err ? t.err : t.line}`, transition: 'border-color .2s',
    '&:focus-within': { borderBottomColor: t.ember },
  });
  const plainInput = { flex: 1, minWidth: 0, background: 'transparent', border: 'none', padding: '9px 2px', fontSize: 16, fontFamily: "'Hanken Grotesk', sans-serif", color: t.text, outline: 'none', '&::placeholder': { color: t.muted, opacity: 0.8 } } as const;
  const selectSx = (err?: string) => ({ ...underline(err), appearance: 'none', WebkitAppearance: 'none', cursor: 'pointer', color: t.text, '& option': { color: '#1C140D' } });
  const tabSx = (active: boolean) => ({
    background: 'none', border: 'none', cursor: 'pointer', p: '0 0 12px', mb: '-1px', whiteSpace: 'nowrap',
    fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 15, color: active ? t.text : t.muted,
    borderBottom: `2px solid ${active ? t.ember : 'transparent'}`, transition: 'color .2s, border-color .2s',
  });

  // ---- submit --------------------------------------------------------------
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (isSignup) {
      const parts = name.trim().split(/\s+/);
      if (parts.length < 2) errs.name = 'Enter your first and last name';
      if (!college) errs.school = 'Choose your school';
      if (!emailLocal.trim()) errs.emailLocal = 'Enter your school email';
      if (!major) errs.major = 'Pick a major';
      if (!year) errs.year = 'Pick a year';
      const pwe = passwordStrengthError(password);
      if (pwe) errs.password = pwe;
      if (!agree) errs.agree = 'Agree to the policies to continue';
      setErrors(errs);
      if (Object.keys(errs).length) return;

      const fullEmail = `${emailLocal.trim()}@${domain}`;
      const [firstName, ...rest] = name.trim().split(/\s+/);
      const lastName = rest.join(' ');
      const res = await register({
        email: fullEmail, password, firstName, lastName, major, year: parseInt(year, 10), hometown: '',
        legalAcceptance: { agree: true, termsVersion: LEGAL_TERMS_VERSION, privacyVersion: LEGAL_PRIVACY_VERSION, cookieVersion: LEGAL_COOKIE_VERSION },
        cookieConsent: { analytics: false, marketing: false },
      });
      if (res.ok && res.requiresVerification) navigate('/verify-email', { state: { email: res.email || fullEmail } });
      else if (res.ok) navigate('/');
    } else {
      if (!EMAIL_RE.test(email.trim())) errs.email = 'Enter a valid email';
      if (!password) errs.password = 'Enter your password';
      setErrors(errs);
      if (Object.keys(errs).length) return;

      const res = await login({ email: email.trim(), password });
      if (res.ok) navigate('/');
      else if (res.needsVerification) navigate('/verify-email', { state: { email: res.email || email.trim() } });
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25, width: '100%', fontFamily: "'Hanken Grotesk', sans-serif", color: t.text }}>
      {/* tabs */}
      <Box role="tablist" sx={{ display: 'flex', gap: '26px', alignItems: 'flex-end', borderBottom: `1px solid ${t.line}` }}>
        <Box component="button" type="button" role="tab" aria-selected={!isSignup} onClick={() => goMode('signin')} sx={tabSx(!isSignup)}>Sign in</Box>
        <Box component="button" type="button" role="tab" aria-selected={isSignup} onClick={() => goMode('signup')} sx={tabSx(isSignup)}>Join</Box>
      </Box>

      <Box component="form" onSubmit={submit} sx={{ display: 'flex', flexDirection: 'column', gap: isSignup ? '15px' : '17px' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <Box component="h2" sx={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 26, lineHeight: 1.04, letterSpacing: '-0.02em', m: 0, color: t.text }}>
            {isSignup ? 'Claim your seat.' : 'Welcome back.'}
          </Box>
          <Box component="p" sx={{ m: 0, fontSize: 14.5, color: t.muted, lineHeight: 1.5 }}>
            {isSignup ? 'One verification and you’re in for good.' : 'Sign in to your campus.'}
          </Box>
        </Box>

        {error && (
          <Box sx={{ ...errSx, display: 'flex', alignItems: 'center', gap: 1, background: t.emberSoft, border: `1px solid ${t.line}`, borderRadius: '2px', p: '8px 12px' }}>
            {error}
          </Box>
        )}

        {/* ---- sign-up only block ---- */}
        <Box aria-hidden={!isSignup} sx={{ display: 'flex', flexDirection: 'column', gap: '15px', overflow: isSignup ? 'visible' : 'hidden', maxHeight: isSignup ? 2000 : 0, opacity: isSignup ? 1 : 0, visibility: isSignup ? 'visible' : 'hidden', transition: 'max-height .45s cubic-bezier(.2,.7,.2,1), opacity .35s ease, visibility .45s' }}>
          {/* full name */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
            <Box component="label" htmlFor="df-name" sx={labelSx}>Full name</Box>
            <Box component="input" id="df-name" type="text" autoComplete="name" placeholder="Alex Rivera"
              value={name} onChange={(e: any) => { setName(e.target.value); clearErr('name'); }} sx={underline(errors.name)} />
            {errors.name && <Box component="span" sx={errSx}>{errors.name}</Box>}
          </Box>

          {/* university combobox */}
          <Box ref={comboRef} sx={{ display: 'flex', flexDirection: 'column', gap: '7px', position: 'relative' }}>
            <Box component="label" htmlFor="df-univ" sx={labelSx}>Your university</Box>
            <Box component="input" id="df-univ" type="text" autoComplete="off" role="combobox"
              aria-expanded={showList} aria-controls="df-univ-list" aria-autocomplete="list"
              aria-activedescendant={highlight >= 0 && filtered[highlight] ? `df-univ-opt-${highlight}` : undefined}
              placeholder="Search your school…" value={schoolQuery}
              onChange={(e: any) => { setSchoolQuery(e.target.value); setListOpen(true); setCollege(null); setHighlight(-1); clearErr('school'); }}
              onFocus={() => setListOpen(true)}
              onKeyDown={onSchoolKeyDown}
              onBlur={(e: any) => { if (comboRef.current && comboRef.current.contains(e.relatedTarget as Node | null)) return; blurTimer.current = setTimeout(() => setListOpen(false), 150); }}
              sx={underline(errors.school)} />
            <Box component="span" aria-hidden sx={{ position: 'absolute', right: 2, bottom: 11, pointerEvents: 'none', color: t.muted, fontSize: 12 }}>▾</Box>
            {showList && (
              <Box id="df-univ-list" role="listbox" aria-label="Schools" sx={{ position: 'absolute', left: 0, right: 0, top: '100%', mt: '6px', zIndex: 60, background: t.bg2, border: `1px solid ${t.line}`, borderRadius: '4px', boxShadow: '0 20px 44px -16px rgba(0,0,0,0.5)', maxHeight: 212, overflowY: 'auto', p: '4px' }}>
                {collegesStatus === 'loading' && (
                  <Box role="status" sx={{ p: '11px 10px', fontSize: 13, color: t.muted }}>Loading schools…</Box>
                )}
                {collegesStatus === 'error' && (
                  <Box component="button" type="button"
                    onMouseDown={(e: any) => e.preventDefault()}
                    onClick={() => loadColleges()}
                    sx={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', p: '11px 10px', borderRadius: '3px', fontSize: 13, color: t.ember, fontWeight: 600, '&:hover': { background: t.emberSoft } }}>
                    Couldn’t load schools — tap to retry
                  </Box>
                )}
                {collegesStatus === 'ready' && filtered.length === 0 && (
                  <Box role="status" sx={{ p: '11px 10px', fontSize: 13, color: t.muted, lineHeight: 1.45 }}>
                    {schoolQuery.trim()
                      ? <>No match for “{schoolQuery.trim()}” — we’re rolling out campus by campus, so yours may not be live yet.</>
                      : 'No campuses are live yet.'}
                  </Box>
                )}
                {collegesStatus === 'ready' && filtered.map((u, i) => (
                  <Box component="button" type="button" role="option" id={`df-univ-opt-${i}`} aria-selected={i === highlight} key={u.slug}
                    onMouseDown={(e: any) => e.preventDefault()}
                    onClick={() => chooseCollege(u)}
                    onMouseEnter={() => setHighlight(i)}
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', width: '100%', boxSizing: 'border-box', textAlign: 'left', background: i === highlight ? t.emberSoft : 'none', border: 'none', cursor: 'pointer', p: '9px 10px', borderRadius: '3px', '&:hover': { background: t.emberSoft } }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                      <Box component="span" sx={{ fontSize: 14, color: t.text, fontWeight: 600 }}>{u.name}</Box>
                      <Box component="span" sx={{ fontSize: 12, color: t.muted, fontFamily: FONT_MONO }}>{u.emailDomains?.[0]}</Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
            {errors.school && <Box component="span" sx={errSx}>{errors.school}</Box>}
          </Box>

          {/* campus-locked chip */}
          {college && (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, alignSelf: 'flex-start', whiteSpace: 'nowrap', p: '7px 13px', borderRadius: 999, background: t.emberSoft, color: t.ember, fontSize: 13, fontWeight: 600 }}>
              <BrandMark size={13} color={t.ember} /> Campus locked · @{domain}
            </Box>
          )}

          {/* school email (local @ domain) */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
            <Box component="label" htmlFor="df-semail" sx={labelSx}>School email</Box>
            <Box sx={row(errors.emailLocal)}>
              <Box component="input" id="df-semail" type="text" autoComplete="off" placeholder="you"
                value={emailLocal} onChange={(e: any) => { setEmailLocal(e.target.value); clearErr('emailLocal'); }} sx={plainInput} />
              {college && <Box component="span" sx={{ color: t.muted, fontSize: 16, whiteSpace: 'nowrap', fontFamily: FONT_MONO }}>@{domain}</Box>}
            </Box>
            {errors.emailLocal && <Box component="span" sx={errSx}>{errors.emailLocal}</Box>}
            {!college && <Box component="span" sx={{ fontSize: 12, color: t.muted }}>Pick your school to lock your campus domain.</Box>}
          </Box>

          {/* major + year */}
          <Box sx={{ display: 'flex', gap: '14px' }}>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '7px' }}>
              <Box component="label" htmlFor="df-major" sx={labelSx}>Major</Box>
              <Box component="select" id="df-major" value={major} onChange={(e: any) => { setMajor(e.target.value); clearErr('major'); }} sx={selectSx(errors.major)}>
                <option value="" disabled>Select…</option>
                {MAJORS.map((m) => <option key={m} value={m}>{m}</option>)}
              </Box>
              {errors.major && <Box component="span" sx={errSx}>{errors.major}</Box>}
            </Box>
            <Box sx={{ width: 96, display: 'flex', flexDirection: 'column', gap: '7px' }}>
              <Box component="label" htmlFor="df-year" sx={labelSx}>Year</Box>
              <Box component="select" id="df-year" value={year} onChange={(e: any) => { setYear(e.target.value); clearErr('year'); }} sx={selectSx(errors.year)}>
                <option value="" disabled>—</option>
                {[1, 2, 3, 4, 5].map((y) => <option key={y} value={y}>{y}</option>)}
              </Box>
              {errors.year && <Box component="span" sx={errSx}>{errors.year}</Box>}
            </Box>
          </Box>
        </Box>

        {/* email (sign-in) */}
        {!isSignup && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
            <Box component="label" htmlFor="df-email" sx={labelSx}>Email</Box>
            <Box component="input" id="df-email" type="email" autoComplete="email" placeholder="you@school.edu"
              value={email} onChange={(e: any) => { setEmail(e.target.value); clearErr('email'); setResetSent(false); }} sx={underline(errors.email)} />
            {errors.email && <Box component="span" sx={errSx}>{errors.email}</Box>}
          </Box>
        )}

        {/* password */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <Box component="label" htmlFor="df-pw" sx={labelSx}>Password</Box>
            {!isSignup && (
              <Box component="a" href="#" onClick={onForgot} sx={{ fontSize: 12, color: t.ember, textDecoration: 'none', fontWeight: 600 }}>Forgot?</Box>
            )}
          </Box>
          <Box sx={row(errors.password)}>
            <Box component="input" id="df-pw" type={showPw ? 'text' : 'password'} autoComplete={isSignup ? 'new-password' : 'current-password'}
              placeholder={isSignup ? 'Create a password' : 'Your password'}
              value={password} onChange={(e: any) => { setPassword(e.target.value); clearErr('password'); }} sx={plainInput} />
            <Box component="button" type="button" onClick={() => setShowPw((s) => !s)} aria-label={showPw ? 'Hide password' : 'Show password'}
              sx={{ background: 'none', border: 'none', color: t.muted, cursor: 'pointer', fontSize: 10.5, fontFamily: FONT_MONO, letterSpacing: '0.1em', p: '4px 2px' }}>
              {showPw ? 'HIDE' : 'SHOW'}
            </Box>
          </Box>
          {errors.password && <Box component="span" sx={errSx}>{errors.password}</Box>}
          {!isSignup && resetSent && (
            <Box component="span" sx={{ fontSize: 12.5, color: t.ember, fontWeight: 600 }}>
              If an account exists for that email, a reset link is on its way — check your inbox.
            </Box>
          )}
        </Box>

        {/* legal (sign-up) */}
        {isSignup && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
              <Checkbox size="small" disableRipple checked={agree} onChange={(e) => { setAgree(e.target.checked); if (e.target.checked) clearErr('agree'); }}
                sx={{ p: 0, m: 0, flexShrink: 0, color: t.muted, '&.Mui-checked': { color: t.ember }, '& .MuiSvgIcon-root': { fontSize: 19 } }} />
              <Box sx={{ fontSize: 12.5, color: t.muted, lineHeight: '19px' }}>
                I agree to the{' '}
                <Link component={RouterLink} to="/terms" sx={{ color: t.ember, fontWeight: 600 }} underline="hover">Terms</Link>,{' '}
                <Link component={RouterLink} to="/privacy" sx={{ color: t.ember, fontWeight: 600 }} underline="hover">Privacy</Link> &{' '}
                <Link component={RouterLink} to="/cookies" sx={{ color: t.ember, fontWeight: 600 }} underline="hover">Cookie</Link> policies.
              </Box>
            </Box>
            {errors.agree && <Box component="span" sx={{ ...errSx, display: 'block', mt: 0.5 }}>{errors.agree}</Box>}
          </Box>
        )}

        {/* submit */}
        <Box component="button" type="submit" disabled={loading}
          sx={{ mt: '4px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '9px', width: '100%', minHeight: 52, whiteSpace: 'nowrap', border: 'none', borderRadius: '2px', cursor: loading ? 'default' : 'pointer', fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 16, color: t.onEmber, background: emberGradient(t), boxShadow: `0 8px 24px -8px ${t.shadow}`, opacity: loading ? 0.8 : 1, transition: 'transform .15s, box-shadow .25s, filter .2s', '&:hover': loading ? {} : { transform: 'translateY(-1px)', filter: 'brightness(1.06)', boxShadow: `0 16px 36px -10px ${t.shadow}` } }}>
          {loading ? (isSignup ? 'Sending…' : 'Signing in…') : (isSignup ? 'Verify my email' : 'Sign in')}
          <Box component="span" aria-hidden sx={{ fontWeight: 700 }}>→</Box>
        </Box>

        {/* verify note */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, fontSize: 12, color: t.muted, lineHeight: 1.45 }}>
          <Box sx={{ flexShrink: 0, mt: '1px' }}><BrandMark size={13} color={t.ember} /></Box>
          <span>{isSignup ? 'We email a one-time verification code. No bots, no outsiders.' : 'Protected campus — verified students only.'}</span>
        </Box>

        {/* switch */}
        <Box component="p" sx={{ m: 0, textAlign: 'center', fontSize: 14, color: t.muted }}>
          {isSignup ? 'Already enrolled? ' : 'New to Dragonsframe? '}
          <Box component="a" href="#" onClick={(e: React.MouseEvent) => { e.preventDefault(); goMode(isSignup ? 'signin' : 'signup'); }} sx={{ color: t.ember, fontWeight: 700, textDecoration: 'none' }}>
            {isSignup ? 'Sign in' : 'Create account'}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AuthForm;
